import React, { useState } from "react";
import {
  Modal,
  Form,
  DatePicker,
  Input,
  Upload,
  Button,
  message,
  Alert,
} from "antd";
import { UploadOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { TextArea } = Input;

const CreateReconciliationModal = ({
  visible,
  onCancel,
  onSubmit,
  loading,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        reconciliationDate: values.reconciliationDate.format("YYYY-MM-DD"),
        notes: values.notes || "",
      };

      // Add file if selected
      if (fileList.length > 0) {
        data.bankStatementFile = fileList[0].originFileObj;
      }

      const success = await onSubmit(data);
      if (success) {
        form.resetFields();
        setFileList([]);
      }
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ].includes(file.type);

      if (!isValidType) {
        message.error("You can only upload PDF, Excel or CSV files!");
        return Upload.LIST_IGNORE;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("File must be smaller than 10MB!");
        return Upload.LIST_IGNORE;
      }

      return false; // Prevent auto upload
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList.slice(-1)); // Only keep the last file
    },
    fileList,
    maxCount: 1,
  };

  // Disable future dates
  const disabledDate = (current) => {
    return current && current > dayjs().endOf("day");
  };

  return (
    <Modal
      title={
        <span>
          <CalendarOutlined style={{ marginRight: 8 }} />
          Create Daily Reconciliation
        </span>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="Create Reconciliation"
      cancelText="Cancel"
      width={500}
    >
      <Alert
        message="Create a New Reconciliation"
        description="This will calculate payment totals for the selected date. The system will automatically aggregate valid payments and suspense entries."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          reconciliationDate: dayjs().subtract(1, "day"), // Default to yesterday
        }}
      >
        <Form.Item
          name="reconciliationDate"
          label="Reconciliation Date"
          rules={[{ required: true, message: "Please select a date" }]}
          extra="Select the date for which you want to create a reconciliation report"
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD MMMM YYYY"
            disabledDate={disabledDate}
            placeholder="Select date"
          />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes (Optional)"
          extra="Add any relevant notes for this reconciliation"
        >
          <TextArea
            rows={3}
            placeholder="Enter any notes or observations..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="bankStatement"
          label="Bank Statement (Optional)"
          extra="Upload a bank statement for comparison (PDF, Excel, or CSV)"
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>
              Select Bank Statement File
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateReconciliationModal;
